// Project standard: ALL displayed times are in IST (Asia/Kolkata, UTC+05:30),
// regardless of the user's device timezone. Backend stores ISO/UTC; the display
// layer converts to IST. See /app/memory/standing_instructions.md.
export const IST_TZ = "Asia/Kolkata";

const pad = (n) => String(n).padStart(2, "0");

/** Return IST parts {y, m, d, hh, mm, ss} for any Date/ISO string. */
function istParts(input) {
  const d = typeof input === "string" ? new Date(input) : input;
  if (!d || isNaN(d.getTime())) return null;
  // en-GB gives dd/mm/yyyy parts; we read them via formatToParts for safety.
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: IST_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts = Object.fromEntries(fmt.formatToParts(d).map((p) => [p.type, p.value]));
  return {
    y: parts.year,
    m: parts.month,
    d: parts.day,
    hh: parts.hour === "24" ? "00" : parts.hour, // some envs return 24
    mm: parts.minute,
    ss: parts.second,
  };
}

export function relTime(iso) {
  if (!iso) return "never";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (!d || isNaN(d.getTime())) return iso;
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

/** Format any ISO/Date as dd.mm.yyyy in IST. */
export function fmtDate(iso) {
  if (!iso) return "—";
  const p = istParts(iso);
  if (!p) return iso;
  return `${p.d}.${p.m}.${p.y}`;
}

/** Format any ISO/Date as dd.mm.yyyy HH:mm in IST. */
export function fmtDateTime(iso) {
  if (!iso) return "—";
  const p = istParts(iso);
  if (!p) return iso;
  return `${p.d}.${p.m}.${p.y} ${p.hh}:${p.mm}`;
}

/** Format any ISO/Date as HH:mm in IST. */
export function fmtTime(iso) {
  if (!iso) return "—";
  const p = istParts(iso);
  if (!p) return iso;
  return `${p.hh}:${p.mm}`;
}

/** "Today" in IST as yyyy-mm-dd (suitable for backend date filters). */
export function todayISO() {
  const p = istParts(new Date());
  if (!p) {
    const d = new Date();
    const tz = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tz).toISOString().slice(0, 10);
  }
  return `${p.y}-${p.m}-${p.d}`;
}

/** yyyy-mm-dd date key in IST for any Date/ISO. */
export function istDateKey(input) {
  const p = istParts(input);
  if (!p) return "";
  return `${p.y}-${p.m}-${p.d}`;
}

/** Format a Date as a weekday short label in IST (e.g., "Mon"). */
export function fmtWeekdayShort(d) {
  return new Intl.DateTimeFormat("en-IN", { timeZone: IST_TZ, weekday: "short" }).format(d);
}

/** Format a Date as a month short label in IST (e.g., "May"). */
export function fmtMonthShort(d) {
  return new Intl.DateTimeFormat("en-IN", { timeZone: IST_TZ, month: "short" }).format(d);
}

/** Day-of-month number in IST. */
export function dayOfMonthIST(d) {
  const p = istParts(d);
  return p ? Number(p.d) : d.getDate();
}
void pad;

export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(file);
  });
}

export function blobToBase64(blob) {
  return fileToBase64(blob);
}

function _getPosition(opts) {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), opts.timeout);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(timer);
        resolve({
          lat: pos.coords.latitude,
          long: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
      },
      () => {
        clearTimeout(timer);
        resolve(null);
      },
      opts,
    );
  });
}

/**
 * Resolve the device's current GPS position. Attendance/cleaning captures
 * require this — without it, an already-captured photo is discarded
 * client-side (GPS is enforced server-side too).
 *
 * High-accuracy GPS often can't get a lock within a few seconds in
 * industrial-park conditions (open sky but far from cell towers, or metal
 * structures blocking signal), which used to make a supervisor's freshly
 * captured photo silently fail to save with no retry — unlike a network
 * outage, which gets queued. We now fall back to a fast, low-accuracy
 * (network/cell-based) fix instead of giving up outright, since that's
 * still accurate enough for the geofence check and dramatically more
 * reliable within a few seconds.
 */
export function getCurrentLocation(timeoutMs = 8000) {
  return (async () => {
    if (!navigator.geolocation) return null;
    const precise = await _getPosition({ enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 30000 });
    if (precise) return precise;
    return _getPosition({ enableHighAccuracy: false, timeout: 5000, maximumAge: 60000 });
  })();
}

export function classNames(...a) {
  return a.filter(Boolean).join(" ");
}


// =================================================== Designation → Category
// Mirrors backend `_cat_of` in salary_report.py. Use this helper anywhere on
// the frontend that buckets a free-text designation into one of:
//   "Sweeper" | "Driver" | "Supervisor"
//
// Contractor wage registers often use "MAZDOOR" / "MAZDUR" / "HELPER" etc.
// for ground workers. They are all canonicalised to "Sweeper" here.
const SUPERVISOR_KEYWORDS = ["supervisor", "incharge", "maistry", "maistri", "mistri"];
const SWEEPER_KEYWORDS = [
  "sweeper", "mazdoor", "mazdur", "helper", "cleaner",
  "labour", "labor", "worker", "unskilled",
];

export function designationCategory(designation) {
  const d = String(designation || "").trim().toLowerCase();
  if (!d) return "Sweeper";
  if (d.includes("driver")) return "Driver";
  if (SUPERVISOR_KEYWORDS.some((k) => d.includes(k))) return "Supervisor";
  if (SWEEPER_KEYWORDS.some((k) => d.includes(k))) return "Sweeper";
  return "Sweeper";
}

/** True if two designations resolve to the same canonical category. */
export function sameCategory(a, b) {
  return designationCategory(a) === designationCategory(b);
}


/**
 * Safely extract a human-readable error message from an axios error.
 *
 * The backend sometimes returns `detail` as a string and sometimes as an
 * object like `{code, message, ...}` (e.g. the 423 device-not-approved
 * response). Passing that object straight to `toast.error()` will crash
 * React with "Objects are not valid as a React child" — which is exactly
 * what happened to the Siruseri contractor whose device was unapproved.
 *
 * This helper always returns a string, falling back to the provided
 * default when nothing useful is available.
 */
export function errorMsg(err, fallback = "Something went wrong") {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.msg === "string") return detail.msg;
    // FastAPI sometimes returns a list of validation errors
    if (Array.isArray(detail) && detail.length) {
      const first = detail[0];
      if (typeof first === "string") return first;
      if (first && typeof first.msg === "string") return first.msg;
    }
  }
  if (typeof err?.message === "string" && err.message) return err.message;
  return fallback;
}
